import i18next from 'i18next';
import dayjs from 'dayjs';
import PhpDateFormatter from 'php-date-formatter';
import { parseDate } from 'chrono-node';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { isDeepEqual } from 'remeda';

dayjs.extend(customParseFormat);

import type {
  TGetDashboardByIdResponseFilters,
  TModuleDisplayOption,
  TModuleDisplayOptionNew,
  TModuleParametersObject,
  TWidget,
} from 'utility/types/dataProvider';
import type { TChartSeriesItem, TChartType } from './types';
import { STALE_TIME } from './constants';
import { toTitleCase } from 'utility/functions';
import { type TDashboardFilters } from 'screens/dashboard/Dashboard';
import {
  barChartData,
  bubbleChartData,
  detailsCardData,
  funnelChartData,
  kpiData,
  lineChartData,
  pieChartData,
  tableData,
  viewsByTrafficSourceBarChartData,
} from './data';
import { DynamicFieldType } from 'components/DynamicFormField/DynamicFormField';
import { getIsSafari } from 'utility/browser';
import {
  DASHBOARD_DATE_FILTER_FORMAT,
  DASHBOARD_DEFAULT_FILTERS,
} from 'screens/dashboard/constants';

// Format chart datapoint.
export const formatChartValue = ({
  value,
  format,
  dateFormat = 'M',
  precision = 2,
  notation = 'standard',
  locale = i18next.language || 'en-US',
}: TModuleDisplayOption): string => {
  switch (format) {
    case 'currency':
    case 'revenue':
    case 'money': {
      const formattedNumber = Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'USD',
        notation,
        maximumFractionDigits: precision,
      }).format(value as number);

      // Remove the decimals this way until Intl.NumberFormat -> trailingZeroDisplay type works.
      if (formattedNumber.endsWith('.00')) {
        return formattedNumber.slice(0, -3);
      }

      return formattedNumber;
    }
    case 'percentage':
    case 'percent': {
      return Intl.NumberFormat(locale, {
        style: 'percent',
        notation,
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(value as number);
    }

    case 'date': {
      // Dates are formatted using the PHP date formatter (https://github.com/kartik-v/php-date-formatter).
      const date = dayjs(value).toDate();
      // If date is invalid, return the original value
      if (!dayjs(value).isValid()) {
        return (value ?? '').toString();
      }
      const formatter = new PhpDateFormatter();
      return formatter.formatDate(date, dateFormat);
    }

    // Raw string values should be returned as-is.
    case 'string':
    case 'text': {
      return (value ?? '').toString();
    }

    // Default to compact notation.
    default: {
      if (typeof value === 'string') {
        return value;
      } else {
        const defaultFormattedNumber = Intl.NumberFormat(locale, {
          notation,
          maximumFractionDigits: precision,
        }).format(value as number);

        // Remove the decimals this way until Intl.NumberFormat -> trailingZeroDisplay type works.
        if (defaultFormattedNumber.endsWith('.00')) {
          return defaultFormattedNumber.slice(0, -3);
        }

        return defaultFormattedNumber;
      }
    }
  }
};

export const parseChartDate = (date: string, dateFormat = 'M d') => {
  const formatter = new PhpDateFormatter();
  return formatter.parseDate(dayjs(date).toDate(), dateFormat);
};

export const convertTimeRangeToMilliseconds = (seconds: number | string) => {
  if (!seconds) return STALE_TIME;
  return Number(seconds) * 1000 || STALE_TIME; // Fallback to STALE_TIME if seconds is 0 (5m).
};

// Get the data point format -- makes assumptions since backend is sending inconsistent structs/schemas.
export const getDatapointFormat = (config: TChartSeriesItem, xDisplayFormat?: string) => {
  switch (true) {
    case !!config?.date_format:
    case !!config?.dateFormat:
    case xDisplayFormat?.toLowerCase() === 'date':
    case config?.label?.toLowerCase()?.includes('date'): // FIXME: This is a grand assumption.
    case config?.format === 'date': {
      return 'date';
    }
    default: {
      return config?.format;
    }
  }
};

type TGetIsWidgetFetchEnabledParams = {
  isMasked: boolean;
  inViewport: boolean;
  shouldWidgetBeFetched: boolean;
  isDashboardMasked: boolean;
  isLiveEnabled: boolean;
  isLive: boolean;
  hasFetched?: boolean;
  id: string | undefined;
};

export const getIsSubscriptionPurchaseEventsWidget = (widgetId: string) =>
  ['subscription.purchaseeventsfunnel', 'subscription.purchaseeventstable'].includes(
    widgetId,
  );

export const getIsWidgetFetchEnabled = ({
  isMasked,
  inViewport,
  shouldWidgetBeFetched,
  isDashboardMasked,
  isLiveEnabled,
  isLive,
  hasFetched,
  id,
}: TGetIsWidgetFetchEnabledParams) => {
  const standardFetchConditions = inViewport && !!id && shouldWidgetBeFetched;

  if (getIsSubscriptionPurchaseEventsWidget(id ?? '')) {
    return false;
  }

  // Do not fetch data if masked.
  if (isMasked || isDashboardMasked) {
    return false;
  }

  // If the widget is in the viewport and normally should be fetched, allow
  // it. For live widgets, also allow fetching when live is enabled.
  if (standardFetchConditions) {
    // Has the live chip.
    if (isLive) {
      // If there isnt any initial data and the chip is off, fetch once.
      if (!hasFetched && !isLiveEnabled) {
        return true;
      }

      if (isLiveEnabled) {
        return true;
      }

      return false;
    }

    if (!isLive) {
      return true;
    }
  }

  return false;
};

export type TRefreshIndicatorType = 'full' | 'dot' | 'none';
type TGetRefreshIndicatorResult = {
  type: TRefreshIndicatorType;
  refresh: number; // seconds
  isLive: boolean;
};

export const getRefreshIndicator = (widget: TWidget): TGetRefreshIndicatorResult => {
  const refreshParam = Number(widget?.parameters?.refresh);
  const refresh =
    refreshParam === 0 || isNaN(refreshParam)
      ? Infinity
      : convertTimeRangeToMilliseconds(refreshParam);

  // If refresh is greater than 1h, update .
  if (refresh > 3600000) {
    return {
      type: 'none',
      refresh,
      isLive: false,
    };
  }

  const isLive = refresh < 60000;

  // 60s or less is considered chip with "live" | "off" text, otherwise it's a svg dot.
  const dotType = refresh <= 60000 && refresh !== 0 ? 'full' : 'dot';

  return {
    type: dotType,
    refresh,
    isLive,
  };
};

/**
 * Returns the pointer to match to the display.options[].
 * @param widget
 * @returns string | undefined
 */
export const getChartConfigName = (widget: TWidget): string | undefined => {
  // If missing widget or missing display options, return null.
  if (!widget || !widget?.module?.display?.options?.length) {
    return widget?.module?.display?.default ?? undefined;
  }

  // Display type api wants us to use.
  let desiredChartType = undefined;

  // CMSADMIN-2002: Check widget.display.options.variation first (highest priority - new API).
  if (widget?.display?.options?.variation) {
    desiredChartType = widget?.display?.options?.variation;
  } else if (!!widget?.display?.view) {
    // Check widget-level view second.
    desiredChartType = widget?.display?.view;
  } else if ('view' in widget?.module?.display) {
    // CMSADMIN-2002: Check module-level next. New property 'view' in module.display to indicate desired chart type.
    desiredChartType = widget?.module?.display?.view;
  } else {
    // Backwards compatibility, fallback.
    desiredChartType = widget?.module?.display?.default;
  }

  return desiredChartType;
};

type TGetChartConfigResult = {
  type: TChartType;
  config: TModuleDisplayOption | undefined;
};

// Get the chart configuration based on the widget's display options.
export const getChartConfig = (widget: TWidget): TGetChartConfigResult => {
  const defaultDisplayOption = widget?.module?.display?.options?.[0];

  // If missing widget or missing display options, return null.
  if (!widget || !widget?.module?.display?.options?.length) {
    return {
      type: defaultDisplayOption?.type as TChartType,
      config: defaultDisplayOption,
    };
  }

  // Display type to use.
  const desiredConfigName = getChartConfigName(widget);

  const config: TModuleDisplayOptionNew | undefined =
    widget?.module?.display?.options?.find(
      (displayOption: TModuleDisplayOption | TModuleDisplayOptionNew) => {
        // CMSADMIN-2002: check for the new name property.
        if ('name' in displayOption) {
          return displayOption.name === desiredConfigName;
        }

        // CMSADMIN-2002: fallback.
        if ('variation' in displayOption) {
          return displayOption.variation === desiredConfigName;
        }

        // Backwards compatibility: Check for type property.
        if ('type' in displayOption) {
          return displayOption.type === desiredConfigName;
        }
      },
    );

  // Handle missing config.
  if (!config) {
    console.warn(
      `Widget (${widget?.id}) display config "${desiredConfigName}" not found, using fallback.`,
    );

    return {
      type: defaultDisplayOption?.type as TChartType,
      config: 'config' in defaultDisplayOption ? defaultDisplayOption.config : undefined,
    };
  }

  // Return the nested config for other chart types.
  return {
    type: config.type as TChartType,
    config: 'config' in config ? config.config : undefined,
  };
};

export const normalizeLabel = (label: string) => {
  if (!label) {
    return 'Unknown';
  }

  // Remove the first character if it's a special character.
  if (label[0].match(/^[^a-zA-Z0-9]/)) {
    label = label.slice(1);
  }

  label = toTitleCase(label) || 'Unknown';
  return label.length > 10 ? `${label.slice(0, 10)}...` : label;
};

type TGetMaskedWidgetDataParams = {
  chartType: TChartType;
  title: string;
  id: string;
};

export const getMaskedWidgetData = ({
  chartType,
  title,
  id,
}: TGetMaskedWidgetDataParams) => {
  switch (chartType) {
    case 'kpi': {
      return {
        widget: {
          ...kpiData.widget,
          title,
        },
        data: kpiData.data,
      };
    }
    case 'pie': {
      return {
        widget: {
          ...pieChartData.widget,
          title,
        },
        data: pieChartData.data,
      };
    }
    case 'bar': {
      if (id === 'editorial.viewsbytrafficsource') {
        return {
          widget: {
            ...viewsByTrafficSourceBarChartData.widget,
            title,
          },
          data: viewsByTrafficSourceBarChartData.data,
        };
      } else {
        return {
          widget: {
            ...barChartData.widget,
            title,
          },
          data: barChartData.data,
        };
      }
    }
    case 'line': {
      return {
        widget: {
          ...lineChartData.widget,
          title,
        },
        data: lineChartData.data,
      };
    }
    case 'funnel': {
      return {
        widget: {
          ...funnelChartData.widget,
          title,
        },
        data: funnelChartData.data,
      };
    }
    case 'detailscard': {
      return {
        widget: {
          ...detailsCardData.widget,
          title,
        },
        data: detailsCardData.data,
      };
    }
    case 'table': {
      return {
        widget: {
          ...tableData.widget,
          title,
        },
        data: tableData.data,
      };
    }
    case 'bubble': {
      return {
        widget: {
          ...bubbleChartData.widget,
          title,
        },
        data: bubbleChartData.data,
      };
    }

    // Return line chart as default.
    default: {
      return {
        widget: {
          ...lineChartData.widget,
          title,
        },
        data: lineChartData.data,
      };
    }
  }
};

/**
 * Widget.module.parameters are not always the same schema.
 * Any reference to widget.module.parameters needs to accommodate the object schema and the array schema.
 * This function normalizes the parameters into an array of DynamicFieldType.
 */
export const normalizeModuleParameters = (widget: TWidget): DynamicFieldType[] => {
  // If parameters is already an array, return as-is.
  if (Array.isArray(widget?.module?.parameters)) {
    return widget.module.parameters as DynamicFieldType[];
  }

  // If parameters is an object, convert to array format.
  if (
    !!widget?.module?.parameters &&
    !Array.isArray(widget?.module?.parameters) &&
    typeof widget?.module?.parameters === 'object'
  ) {
    return Object.entries(widget.module.parameters as TModuleParametersObject).map(
      ([key, dynamicFieldConfig]) => ({
        // @ts-expect-error
        name: key, // set the name from the object key, in case it's not set in the DynamicFieldType.
        ...dynamicFieldConfig,
      }),
    ) as DynamicFieldType[];
  }

  return [];
};

// Dashboard filters require that dateRangeFilter be split into startDate and endDate.
// Filters out query parameters that are not applicable to widget, but exist in dashboard filters.
// See getInitDashboardFilters
export const normalizeDashboardFiltersQuery = (
  dashboardFilters: TDashboardFilters,
  widget: TWidget,
  isCustomDashboard: boolean = false,
) => {
  const filters: TDashboardFilters = Object.assign({}, dashboardFilters);
  const normalizedModuleParameters = normalizeModuleParameters(widget);
  const hardsetWidgetFilters: Record<string, any> = isCustomDashboard
    ? getInitDashboardOrWidgetFiltersFromWidgetParameters(widget)
    : {};
  const widgetFilterParams = Boolean(normalizedModuleParameters?.length)
    ? normalizedModuleParameters?.map((param: DynamicFieldType) => param?.name)
    : [];

  // Hard-set widget parameters must win for date query params.
  const hardsetDateRange = Array.isArray(hardsetWidgetFilters?.dateRangeFilter)
    ? (hardsetWidgetFilters.dateRangeFilter as [string, string])
    : undefined;
  const hardsetStartDate =
    hardsetWidgetFilters?.startDate ?? hardsetDateRange?.[0] ?? undefined;
  const hardsetEndDate =
    hardsetWidgetFilters?.endDate ?? hardsetDateRange?.[1] ?? undefined;

  const allowedFilterParams = new Set<string>(widgetFilterParams);
  Object.keys(hardsetWidgetFilters || {}).forEach((key) => {
    allowedFilterParams.add(key);
  });
  if (allowedFilterParams.has('dateRangeFilter')) {
    allowedFilterParams.add('startDate');
    allowedFilterParams.add('endDate');
  }
  if (hardsetStartDate || hardsetEndDate) {
    allowedFilterParams.add('startDate');
    allowedFilterParams.add('endDate');
  }

  // Process dateRangeFilter BEFORE filtering to preserve the data transformation.
  if (filters?.dateRangeFilter && !hardsetStartDate && !hardsetEndDate) {
    // iso date strings.
    const [startDate, endDate] = filters.dateRangeFilter as [string, string];
    filters.startDate = dayjs(startDate).toISOString();
    filters.endDate = dayjs(endDate).toISOString();
  }

  // Remove dateRangeFilter from query payload.
  // Cast to any to avoid TypeScript errors if type doesn't include this field.
  delete filters.dateRangeFilter;

  // Handle cases where startDate and endDate are directly provided.
  if (filters?.startDate) {
    filters.startDate = dayjs(filters.startDate).toISOString();
  }
  if (filters?.endDate) {
    filters.endDate = dayjs(filters.endDate).toISOString();
  }

  // Hard-set widget dates override dashboard/user dates.
  if (hardsetStartDate) {
    filters.startDate = dayjs(hardsetStartDate).toISOString();
  }
  if (hardsetEndDate) {
    filters.endDate = dayjs(hardsetEndDate).toISOString();
  }

  // Filter out any widget-specific parameters from the dashboard filters.
  // When widget has no parameters, remove all filters.
  if (allowedFilterParams.size > 0) {
    Object.keys(filters).forEach((key: string) => {
      if (!allowedFilterParams.has(key)) {
        delete filters[key as keyof TDashboardFilters];
      }
    });
  } else {
    // If widget has no parameters, clear all filters.
    Object.keys(filters).forEach((key: string) => {
      delete filters[key as keyof TDashboardFilters];
    });
  }

  // Special case for platform "all" (remove it).
  // See DASHBOARD_DEFAULT_FILTERS comment.
  if (filters?.platform && filters.platform === 'all') {
    delete filters.platform;
  }

  // Remove any undefined, null, or empty values.
  Object.keys(filters).forEach((key) => {
    const currentFilter: unknown = filters[key as keyof TDashboardFilters];

    const hasValidValue =
      currentFilter !== undefined &&
      currentFilter !== null &&
      currentFilter !== '' &&
      !(Array.isArray(currentFilter) && (currentFilter as any[])?.length === 0);

    if (!hasValidValue) {
      delete filters[key as keyof TDashboardFilters];
    }
  });

  // CMSADMIN-2110 - Users Dashboard - Audience+ Audience Sizes: Remove audience_allow prop.
  // API currently cannot handle the audience_allow parameter in comma notation
  // (current standard for dashboards [getWidgetDataById]), so it must be removed.
  if (widget?.id === 'subscription.audiencesizes' && !!filters?.audience_allow) {
    delete filters.audience_allow;
  }

  return filters;
};

// Return an object with parameter names as keys and their default values.
export const getInitWidgetParameters = (
  widget: TWidget,
  exportStartAndEndDate: boolean = false,
) => {
  const normalizedModuleParameters = normalizeModuleParameters(widget);

  // If no parameters, return default filters.
  if (!normalizedModuleParameters?.length) {
    if (exportStartAndEndDate) {
      const [startDate, endDate] = DASHBOARD_DEFAULT_FILTERS?.dateRangeFilter || [];

      return {
        ...DASHBOARD_DEFAULT_FILTERS,
        startDate: dayjs(startDate).toISOString(),
        endDate: dayjs(endDate).toISOString(),
      };
    }

    return DASHBOARD_DEFAULT_FILTERS;
  }

  const reduced = normalizedModuleParameters?.reduce(
    (acc: Record<string, any>, param: any) => {
      if (param && typeof param === 'object' && 'name' in param && 'default' in param) {
        return { ...acc, [param.name]: param.default };
      }
      return acc;
    },
    {},
  );

  if (exportStartAndEndDate) {
    const [startDate, endDate] = reduced?.dateRangeFilter || [];

    return {
      ...reduced,
      startDate: dayjs(startDate).toISOString(),
      endDate: dayjs(endDate).toISOString(),
    };
  }

  return reduced;
};

// Compute a Dayjs instance for maxDate:
// - If rawMax is a string, try to parse with chrono and fall back to dayjs(rawMax)
// - If rawMax is a Date/number/Dayjs-like, pass it to dayjs()
// - If missing, default to yesterday
export const getComputedMaxDate = (rawMax?: any): dayjs.Dayjs =>
  rawMax
    ? typeof rawMax === 'string'
      ? (() => {
          const parsed = parseDate(rawMax);
          return parsed ? dayjs(parsed) : dayjs(rawMax);
        })()
      : dayjs(rawMax as any)
    : dayjs().subtract(1, 'day');

// Fallback date parser for Safari (chrono-node doesn't work in Safari)
export const parseDatePhraseFallback = (phrase: string): Date | null => {
  if (!phrase) return null;

  const trimmed = phrase.toLowerCase().trim();

  // Handle "now"
  if (trimmed === 'now') {
    return dayjs().toDate();
  }

  // Handle "today"
  if (trimmed === 'today') {
    return dayjs().toDate();
  }

  // Handle "yesterday"
  if (trimmed === 'yesterday') {
    return dayjs().subtract(1, 'day').toDate();
  }

  // Handle "today -X days" or "today +X days"
  const relativeMatch = trimmed.match(/^today\s*([+-])\s*(\d+)\s*days?$/);
  if (relativeMatch) {
    const [, sign, amount] = relativeMatch;
    const days = parseInt(amount, 10);
    return sign === '-'
      ? dayjs().subtract(days, 'day').toDate()
      : dayjs().add(days, 'day').toDate();
  }

  // Handle "X days ago"
  const daysAgoMatch = trimmed.match(/^(\d+)\s*days?\s*ago$/);
  if (daysAgoMatch) {
    const days = parseInt(daysAgoMatch[1], 10);
    return dayjs().subtract(days, 'day').toDate();
  }

  // Fallback: try parsing as a date string with dayjs
  const parsed = dayjs(phrase);

  console.info('safari date phrase parsing', {
    phrase,
    parsed: parsed.toString(),
  });

  return parsed.isValid() ? parsed.toDate() : null;
};

// Parse dashboard filters from API response to initial filter values for the dashboard.
export const getInitDashboardOrWidgetFiltersFromModuleDynaFields = (
  config: TGetDashboardByIdResponseFilters,
) => {
  if (!config || !config?.length) {
    return {};
  }

  const filters = config?.reduce(
    (acc, field) => {
      switch (field.name) {
        case 'dateRangeFilter': {
          if (field.type === 'group') {
            // Default values for dateRangeFilter group (startDate, endDate) will be in field.config.children
            const defaultChildren = field.config?.children || [];

            // Returned as phrases like: "today -30 days" from the api.
            let defaultStartDatePhrase =
              (defaultChildren?.find((child) => child.name === 'startDate')
                ?.default as string) || '';
            const defaultEndDatePhrase =
              (defaultChildren?.find((child) => child.name === 'endDate')
                ?.default as string) || '';

            // Special handling: convert "today -30 days" to "today -31 days" when paired with "yesterday."
            // Need to add an extra day so the "Last 30 Days" quick date range button is active.
            if (
              defaultStartDatePhrase === 'today -30 days' &&
              defaultEndDatePhrase === 'yesterday'
            ) {
              defaultStartDatePhrase = 'today -31 days';
            }

            // Same for "today -7 days" + "yesterday" to keep "Last 7 Days" quick date range button active.
            if (
              defaultStartDatePhrase === 'today -7 days' &&
              defaultEndDatePhrase === 'yesterday'
            ) {
              defaultStartDatePhrase = 'today -8 days';
            }

            let defaultStartDate = DASHBOARD_DEFAULT_FILTERS?.dateRangeFilter?.[0];
            let defaultEndDate = DASHBOARD_DEFAULT_FILTERS?.dateRangeFilter?.[1];

            // Parse date phrases using chrono-node (or fallback for Safari).
            const useSafariFallback = getIsSafari();

            if (defaultStartDatePhrase) {
              const parsedStartDate = useSafariFallback
                ? parseDatePhraseFallback(defaultStartDatePhrase)
                : parseDate(defaultStartDatePhrase);
              if (parsedStartDate) {
                defaultStartDate = dayjs(parsedStartDate).format(
                  DASHBOARD_DATE_FILTER_FORMAT,
                );
              }
            }

            if (defaultEndDatePhrase) {
              const parsedEndDate = useSafariFallback
                ? parseDatePhraseFallback(defaultEndDatePhrase)
                : parseDate(defaultEndDatePhrase);
              if (parsedEndDate) {
                defaultEndDate = dayjs(parsedEndDate).format(
                  DASHBOARD_DATE_FILTER_FORMAT,
                );
              }
            }

            // Use month-day-year format (no time) to keep values simple for UI/tests
            // normalizeDashboardFiltersQuery() formats the dates to iso strings for the payload.
            acc[field.name] = [defaultStartDate, defaultEndDate];
          }
          break;
        }

        case 'startDate':
        case 'endDate': {
          // Convert the phrase to an actual date string in MM-DD-YYYY format.
          // If there isn't a default, skip it to avoid overwriting dateRangeFilter defaults.
          if (field?.default) {
            const defaultDatePhrase = field.default as string;
            const useFallback = getIsSafari();
            const parsedDate = useFallback
              ? parseDatePhraseFallback(defaultDatePhrase)
              : parseDate(defaultDatePhrase);
            if (parsedDate) {
              acc[field.name] = dayjs(parsedDate).format(DASHBOARD_DATE_FILTER_FORMAT);
            }
          }
          break;
        }

        case 'platform': {
          // CMSADMIN-1810 - respect dynamic field default value for platform
          // NOTE: See comment at "src\screens\dashboard\constants.ts"
          const apiDefault = field?.default;
          acc[field.name] = apiDefault;
          break;
        }

        default: {
          // Add a dateRangeFilter if there isn't one already.
          // Enables the flow to work properly:
          // DashboardControls -> QuickDateRange -> Custom Date Range.
          if (!acc.dateRangeFilter) {
            acc.dateRangeFilter = [
              dayjs().subtract(31, 'day').format(DASHBOARD_DATE_FILTER_FORMAT),
              dayjs().subtract(1, 'day').format(DASHBOARD_DATE_FILTER_FORMAT),
            ];
          }

          // Default handling for other field types.
          if (field.type === 'text' || field.type === 'select') {
            acc[field.name] = field.default ?? '';
          } else {
            acc[field.name] = field.default ?? null;
          }

          break;
        }
      }
      return acc;
    },
    {} as Record<string, any>,
  );

  return filters;
};

export const getInitDashboardOrWidgetFiltersFromWidgetParameters = (widget: TWidget) => {
  return widget?.parameters ?? {};
};

/**
 * CMSADMIN-1810: Normalize for applied badge count comparison.
 *
 * For example multiselect (eg: platform) comes in as `null` for its
 * dynfield default value, but via interacting with the filter, it may
 * become an empty array. (eg: select, apply, deselect, apply)...
 * At that point the form may have value of empty array.
 * Normalizes so comparison treats both `null` and `[]` as the null.
 *
 * Keeping scoped per-field for now, but likely can be generalized
 * per field type (eg: denum, enum, etc) instead of matching
 * specific cases like platform and hostname.
 */

type NormalizeValueForComparisonArgs = {
  value: any;
  field: DynamicFieldType;
};

function normalizeValueForComparison({
  value,
  field,
}: NormalizeValueForComparisonArgs): any {
  // Platform default can be null while an
  // empty multiselect produces an empty array.
  if (
    field?.name === 'platform' &&
    field?.config?.multiple === true &&
    Array.isArray(value) &&
    value.length === 0
  ) {
    return null;
  }

  // Hostname API default is a string (even though its a multi/denum)
  // but our dynamic field ui / RHF produces array after interaction.
  if (
    field?.name === 'hostname' &&
    field?.config?.multiple === true &&
    typeof value === 'string'
  ) {
    return [value];
  }

  return value;
}

type IsFilterChangedArgs = {
  formValue: any;
  field: DynamicFieldType;
};

/**
 * CMSADMIN-1810: Normalize for applied badge count comparison.
 * Normalized compare of current RHF vs API DynField default.
 */
export function isFilterChanged({ formValue, field }: IsFilterChangedArgs): boolean {
  const nFormValue = normalizeValueForComparison({ value: formValue, field: field });
  const nDefaultValue = normalizeValueForComparison({
    value: field.default,
    field: field,
  });
  const isEqual = isDeepEqual(nFormValue, nDefaultValue);
  const isChanged = !isEqual;
  return isChanged;
}
